import { Button } from "../../../../../components/common/Button";
import {
  ButtonsWrapper,
  ContentWrapper,
  DetailsBox,
  DetailsContent,
  DetailsRow,
  DetailsValue,
} from "../styles";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { merchantBusinessKeys } from "../../../../Settings/settingsSlice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../utils/encryptionAlgorithm";
import { KlashaCNYQuotation, payoutRequestV2, setLoading } from "../../../redux/slice";
import Loader from "../../../../../components/common/Loader";
import { NetworkErrorAlertContext } from "../../../../../context/NetworkErrorAlert";
import WalletOTP from "../../../components/SendFunds/components/WalletOTP";
import { requestPayoutV2Otp } from "../../../api";
import { getErrorMessage } from "../../../utils";
import {
  DetailsLabel,
  DetailsSectionHeader,
  EditButton,
  SectionTitle,
} from "../../../components/Withdrawal/styles";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatCurrencyAmount,
  getCountryName,
} from "../../../../../utils/currency";
import dayjs from "dayjs";
import { formatValue } from "../../../utils";
import { clearUserData, savedCNYData, setStep } from "../../../redux/formDataSlice";
import { SuccessAlertContext } from "../../../../../context/SuccessAlertContext";
import { paymentMethodConfigurations } from "../../../const";

type Props = {
  handlePrevious?: () => void;
};

type SenderAddress = {
  city?: string;
  streetAddress?: string;
  countryCode?: string;
  postcode?: string;
  state?: string;
};

type Logistics = {
  company?: string;
  orderNo?: string;
};

type Product = {
  name?: string;
  quantity?: string;
  website?: string;
};

type Document = {
  totalAmount?: string;
  attachments?: Array<{ fileId: string }>;
  orderNumber?: string;
  orderTime?: string;
  orderCurrency?: string;
  logistics?: Logistics;
  tradeType?: string;
  products?: Product[];
  website?: string;
};

type PayoutRequest = {
  receiverIdType?: string;
  receiverLastName?: string;
  cardNumber?: string;
  receiverFirstName?: string;
  senderAddress?: SenderAddress;
  cardHolderName?: string;
  bankCode?: string;
  senderNationality?: string;
  senderIdType?: string;
  purpose?: string;
  quotationId?: string;
  accountName?: string;
  creditAccountCountry?: string;
  accountType?: string;
  senderFirstName?: string;
  senderLastName?: string;
  receiverIdNumber?: string;
  senderIdNumber?: string;
  bankName?: string;
  accountNumber?: string;
  requestId?: string;
  receiverMobileNumber?: string;
  receiverEmail?: string;
  senderBirthDate?: string;
  receiverRelationship?: string;
  accountId?: string;
  receiverBusinessIdType?: string;
  receiverBusinessRegisteredName?: string;
  receiverBusinessAddress?: SenderAddress;
  receiverBusinessMobileNumber?: string;
  senderBusinessRegisteredName?: string;
  senderBusinessAddress?: SenderAddress;
  document?: Document;
  receiverBusinessIdNumber?: string;
  tradeType?: string;

};

const Review = ({ handlePrevious }: Props) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);
  const [verifyOTP, setVerifyOTP] = useState(false);
  const { currencyCode } = useParams();
  const { businessId } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);
  const { loading } = useAppSelector((state) => state.wallets || {});


  const cardHolderFullName = `${getSavedData?.cardholderFirstName} ${getSavedData?.cardholderLastName}`;
  const senderFullName = `${getSavedData?.senderFirstName} ${getSavedData?.senderLastName}`;
  const beneficiaryFullName = `${getSavedData?.beneficiaryFirstName} ${getSavedData?.beneficiaryLastName}`;
  const totalAmount = getSavedData.sourceAmount + getSavedData.fee;
  const isB2B = getSavedData?.paymentMethod === "bank_account_business";


  const PaymentType =
    getSavedData?.paymentMethod === "unionpay" ? "Card" : "Bank account";

  const currentDate = dayjs().format("YYYY-MM-DD");

  const senderCountry = getCountryName(getSavedData?.senderCountry);

  const { onShowAlert: onShowSuccessAlert } =
    useContext(SuccessAlertContext) || {};
  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const publicKey = keys?.data?.publicKey;

  /* Note: Needed to update the quote when an error occurs. Feel free to abstract this and what's in the amount step into a utils */
  const getKlashaQuotation = useCallback(async () => {
    if (!businessId) return;

    const config = paymentMethodConfigurations[getSavedData?.paymentMethod] || {
      serviceCode: "",
      service: "",
      fundSource: "",
    };

    const data = {
      sourceCurrency: "USD",
      destinationCurrency: "CNY",
      destinationAmount: getSavedData?.destinationAmount,
      ...config,
    };

    try {
      const { encryptedMessage, publicKey: jitPublicKey } = await encryptPayloadWithMerchantKeyJIT(
        businessId,
        data,
      );
      const payload = { message: encryptedMessage };
      const token = jitPublicKey || keys?.data?.publicKey;

      const action = await dispatch(
        KlashaCNYQuotation({ message: payload, token })
      );

      if (KlashaCNYQuotation.fulfilled.match(action)) {
        const { fxRate, fee, sourceAmount, id, reference } = action.payload;
        dispatch(savedCNYData({
          quotationId: id,
          reference: reference,
          rate: fxRate,
          fee: fee,
          sourceAmount: sourceAmount,
        }));

        onShowSuccessAlert("We are updating the quotation for you. Hang in there!")

      }

      else if (KlashaCNYQuotation.rejected.match(action)) {
        onShowErrorAlert("", action.payload as string);
      }
    } catch (err: any) {
      onShowErrorAlert("", getErrorMessage(err, "Failed to update quotation"));
    }
  }, [
    businessId,
    dispatch,
    getSavedData?.destinationAmount,
    getSavedData?.paymentMethod,
    keys?.data?.publicKey,
    onShowErrorAlert,
    onShowSuccessAlert,
  ]);





  const handlePayout = async (otp?: string) => {

    const payoutRequest: PayoutRequest = {
      receiverLastName: getSavedData?.beneficiaryLastName,
      bankCode: getSavedData?.bankCode,
      receiverFirstName: getSavedData?.beneficiaryFirstName,
      bankName: getSavedData?.bankName,
      senderAddress: {
        ...getSavedData?.senderAddress,
      },
      senderNationality: getSavedData?.senderCountry,
      senderIdType: getSavedData?.senderID,
      purpose: getSavedData?.paymentPurpose,
      quotationId: getSavedData?.quotationId,
      accountName: getSavedData?.accountName,
      accountType: getSavedData?.beneficiary,
      tradeType: "GOODS",
      senderFirstName: getSavedData?.senderFirstName,
      senderLastName: getSavedData?.senderLastName,
      receiverIdNumber: getSavedData?.beneficiaryIdNumber,
      senderIdNumber: getSavedData?.senderIdNumber,
      accountNumber: getSavedData?.accountNumber,
      requestId: getSavedData?.reference,
      receiverMobileNumber: getSavedData?.beneficiaryPhoneNumber,
      receiverEmail: getSavedData?.beneficiaryEmail,
      creditAccountCountry: "CN",
      cardNumber: getSavedData?.cardNumber?.replace(/\s+/g, "") ?? null,
      cardHolderName: cardHolderFullName,
      senderBirthDate: getSavedData?.senderBirthDate,
      receiverRelationship: getSavedData?.relationship,
      document: {
        totalAmount: getSavedData?.totalBeneficiaryAmount,
        website: getSavedData?.businessWebsite,
        attachments: [
          {
            fileId: getSavedData?.commercialDocument,
          },
        ],
        orderNumber: getSavedData?.orderNumber,
        orderTime: getSavedData?.orderDate,
        orderCurrency: "CNY",
        logistics: {
          company: getSavedData?.productName,
          orderNo: getSavedData?.orderNumber,
        },
        tradeType: "GOODS",
        products: [
          {
            name: getSavedData?.productName,
            quantity: getSavedData?.productNumber,
          },
        ],
      },
      receiverBusinessIdType: "ID_CARD",
      receiverBusinessRegisteredName: getSavedData?.beneficiaryBusinessLegalName,
      receiverBusinessMobileNumber: getSavedData?.beneficiaryPhoneNumber,
    };

    if (isB2B) {
      payoutRequest.receiverBusinessAddress = {
        city: getSavedData?.beneficiaryCity,
        streetAddress: getSavedData?.beneficiaryStreet,
        countryCode: getSavedData?.beneficiaryCountry,
        postcode: getSavedData?.beneficiaryPostcode,
        state: getSavedData?.beneficiaryCity,
      };

      payoutRequest.senderBusinessAddress = {
        ...getSavedData?.senderAddress,
      };

      payoutRequest.receiverIdType = getSavedData?.beneficiaryID;

      payoutRequest.senderIdNumber = getSavedData?.senderIdNumber;
      payoutRequest.receiverIdNumber = getSavedData?.beneficiaryIdNumber;
      payoutRequest.senderBirthDate = getSavedData?.senderBirthDate;
      payoutRequest.receiverRelationship = getSavedData?.relationship;
      payoutRequest.receiverBusinessIdNumber = getSavedData?.beneficiaryBusinessNumber
      payoutRequest.senderBusinessRegisteredName = getSavedData?.senderBusinessName;

    }

    else if (getSavedData?.paymentMethod === "wechatpay") {
      payoutRequest.accountId = getSavedData?.accountIdTypes;
      payoutRequest.accountNumber = getSavedData?.accountPhoneNumber.replace("+", "");
    }

    else if (getSavedData?.paymentMethod === "alipay") {
      payoutRequest.accountId = getSavedData?.accountIdTypes;
      payoutRequest.accountNumber = getSavedData?.accountPhoneNumber?.replace("+", "") || getSavedData?.accountEmail;

    }

    const filteredPayoutRequest = Object.fromEntries(
      Object.entries({ ...payoutRequest, otp }).filter(([key, value]) => value !== undefined)
    );

    if (!businessId) {
      onShowErrorAlert?.("", "Business information is not available. Please try again.");
      return;
    }

    try {
      const { encryptedMessage, publicKey: jitPublicKey } = await encryptPayloadWithMerchantKeyJIT(
        businessId,
        filteredPayoutRequest,
      );

      const payload = JSON.stringify({
        message: encryptedMessage,
      });

      const token = jitPublicKey || publicKey;

      const action = await dispatch(
        payoutRequestV2({ data: payload, token })
      );

      if (payoutRequestV2.fulfilled.match(action)) {
        onShowSuccessAlert?.("Payout successful");
        dispatch(clearUserData());
        navigate("/wallets");
      } else if (payoutRequestV2.rejected.match(action)) {
        onShowErrorAlert?.("", action.payload as string);
        await getKlashaQuotation();
      }
    } catch (err: any) {
      const message = getErrorMessage(err, "Failed to initiate payout");
      onShowErrorAlert?.("", message);
    }
  };





  const handleSendOTP = async () => {
    try {
      dispatch(setLoading(true));
      await requestPayoutV2Otp(getSavedData?.reference, publicKey);
      setVerifyOTP(true);
    } catch (error) {
      onShowErrorAlert("", getErrorMessage(error, "Failed to send OTP"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePrevStep = (index: number) => {
    dispatch(setStep(index));
  };


  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      {verifyOTP && (
        <WalletOTP
          setVerifyOTP={setVerifyOTP}
          makePayment={handlePayout}
          mode="transaction"
          onResend={handleSendOTP}
        />
      )}

      <DetailsBox>
        <DetailsSectionHeader>
          <SectionTitle>Transaction details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(0)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Amount sent</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(currencyCode, getSavedData?.amount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Exchange rate</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("USD", getSavedData?.rate)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Processing fee</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("USD", getSavedData?.fee)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Total amount charged</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("USD", totalAmount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Purpose</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.paymentPurpose)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Payment method</DetailsLabel>
            <DetailsValue>{PaymentType}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Date</DetailsLabel>
            <DetailsValue>{currentDate}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Sender details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(1)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Sender type</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.receiverAccountType)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Sender name</DetailsLabel>
            <DetailsValue>{senderFullName}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Sender country</DetailsLabel>
            <DetailsValue>{senderCountry}</DetailsValue>
          </DetailsRow>

          {isB2B && (
            <DetailsRow>
              <DetailsLabel>Website</DetailsLabel>
              <DetailsValue>{getSavedData?.businessWebsite}</DetailsValue>
            </DetailsRow>
          )}

        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Beneficiary's details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(2)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>
        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary type</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.accountType)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Beneficiary name</DetailsLabel>
            <DetailsValue>{beneficiaryFullName}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Phone number</DetailsLabel>
            <DetailsValue>{getSavedData?.beneficiaryPhoneNumber}</DetailsValue>
          </DetailsRow>
        </DetailsContent>
      </DetailsBox>

      <ButtonsWrapper>
        <Button
          onClick={handlePrevious}
          theme="alternate"
          height="48px"
          width="203px"
        >
          Back
        </Button>
        <Button
          onClick={handleSendOTP}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default Review;